import {
  ChangeDetectorRef,
  Component,
  HostBinding,
  Input,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { iValidatedControl } from '../iValidatedControl';
import { BaseCustomControl } from '../baseCustomControl';

@Component({
  selector: 'app-multiline-input',
  templateUrl: './multiline-input.component.html',
  providers: [
    // see https://blog.thoughtram.io/angular/2016/07/27/custom-form-controls-in-angular-2.html
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => MultilineInputComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class MultilineInputComponent
  extends BaseCustomControl
  implements ControlValueAccessor, iValidatedControl
{
  @HostBinding('class.formField') formFieldClass = true; // Always applied
  @HostBinding('class.readOnly') get isReadOnly() {
    return this.readonly;
  }

  // UI:
  @Input() rows: number = 5;
  @Input() cols: number = 40;
  @Input() validationLocation: string = 'right'; // right | under

  // Options:
  @Input() maxLength: number | undefined;

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
    this.illegalCharsRegEx = new RegExp(/[\>\<]/g);
  }

  get value(): string {
    if (this.boundValue) {
      return this.boundValue;
    }
    return '';
  }

  public override validate(): boolean {
    this.isValid = true;
    this.validationMsg = '';

    if (this.required && this.value == '') {
      this.isValid = false;
      this.validationMsg = 'required';
    } else if (!this.required && this.value == '') {
      this.isValid = true;
    }

    if (!this.isValid) {
      this._handle_validateFailed();

      if (this.validationMsg == '') {
        this.validationMsg = 'invalid';
      }
    } else {
      this._handle_validatePassed();
    }

    return this.isValid;
  }

  _handle_onKeyUp(e: KeyboardEvent) {
    // Here, we're only going to test the current key stroke if it's
    // not a non-character (like backspace or arrow)

    if (this.illegalCharsRegEx) {
      this.boundValue = this.value.replaceAll(this.illegalCharsRegEx, '');
    }

    if (
      this.validateOn == 'change' &&
      e.code != 'Backspace' &&
      e.code != 'Delete'
    ) {
      // If validation failed before, and the user is deleting or backspacing,
      // let's clear the message while the user is typing so it doesn't appear that what
      // they're typing still fails validation

      this.validate();
    } else {
      // If validation failed before, let's clear the message
      // while the user is typing so it doesn't appear that what
      // they're typing still fails validation
      this.isValid = true;
      this._clearValidationMessage();
    }

    this.onChange(this.boundValue);

    if (this.onChanged) {
      this.onChanged.emit(this.boundValue);
    }

    return true;
  }
}
